

import { useState, useRef, useEffect } from "react"
import { Document, Page, pdfjs } from "react-pdf"
import { Image, Type, Eraser, ZoomIn, ZoomOut, Clipboard, Square, Trash2, Save, Circle } from "lucide-react"
import katex from "katex"
import "katex/dist/katex.min.css"

// Custom styles for the range input
const rangeStyles = `
  input[type="range"] {
    -webkit-appearance: none;
    appearance: none;
    height: 8px;
    background: #e5e7eb;
    border-radius: 5px;
    outline: none;
  }
  
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: #3b82f6;
    border-radius: 50%;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  
  input[type="range"]::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: #3b82f6;
    border-radius: 50%;
    cursor: pointer;
    transition: background 0.15s ease;
    border: none;
  }
  
  input[type="range"]::-webkit-slider-thumb:hover {
    background: #2563eb;
  }
  
  input[type="range"]::-moz-range-thumb:hover {
    background: #2563eb;
  }
`

// Set the worker source for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.mjs`

const PdfViewer = ({ file }) => {
  const [numPages, setNumPages] = useState(null)
  const [scale, setScale] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [mode, setMode] = useState("view") // view, draw, text, image, delete
  const [drawColor, setDrawColor] = useState("#000000")
  const [drawSize, setDrawSize] = useState(2)
  const [isDrawing, setIsDrawing] = useState(false)
  const [textInput, setTextInput] = useState("")
  const [isLatex, setIsLatex] = useState(false)
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 })
  const [annotations, setAnnotations] = useState({})
  const [selectedImage, setSelectedImage] = useState(null)
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 })
  const [showTextPosition, setShowTextPosition] = useState(false)
  const [imageSelectionMode, setImageSelectionMode] = useState("rectangle") // rectangle, roundedRect, or circle
  const [rectangleStart, setRectangleStart] = useState({ x: 0, y: 0 })
  const [rectangleEnd, setRectangleEnd] = useState({ x: 0, y: 0 })
  const [isSelectingRectangle, setIsSelectingRectangle] = useState(false)
  const [selectedFont, setSelectedFont] = useState("Arial")
  const [fontSize, setFontSize] = useState(16)
  const [selectedAnnotation, setSelectedAnnotation] = useState(null)
  const [deleteOpacity, setDeleteOpacity] = useState(1) // Opacity for delete rectangles
  // Add a new state for selected image
  const [selectedImageIndex, setSelectedImageIndex] = useState(null)

  // Add a history state to track changes for undo functionality
  // Add this after other state declarations
  const [history, setHistory] = useState({})

  const containerRef = useRef(null)
  const canvasRef = useRef(null)
  const fileInputRef = useRef(null)
  const latexPreviewRef = useRef(null)

  const onDocumentLoadSuccess = (pdf) => {
    setNumPages(pdf.numPages)
    setCurrentPage(1)
    // Initialize annotations object for all pages
    const newAnnotations = {}
    for (let i = 1; i <= pdf.numPages; i++) {
      newAnnotations[i] = {
        drawings: [],
        texts: [],
        images: [],
        deletions: [], // Add deletions array for white rectangles
      }
    }
    setAnnotations(newAnnotations)
  }

  // Adjust scale dynamically based on container width
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const pdfWidth = 800 // Default width of a PDF page in points (approx.)
        const newScale = (containerWidth / pdfWidth) * 0.5 // Adjust scale with some margin
        setScale(newScale)
      }
    }

    updateScale() // Set scale on mount
    window.addEventListener("resize", updateScale) // Update on resize
    return () => window.removeEventListener("resize", updateScale)
  }, [])

  // LaTeX preview
  useEffect(() => {
    if (isLatex && textInput && latexPreviewRef.current) {
      try {
        katex.render(textInput, latexPreviewRef.current, {
          throwOnError: false,
          displayMode: true,
        })
      } catch (error) {
        latexPreviewRef.current.textContent = "LaTeX Error: " + error.message
      }
    }
  }, [textInput, isLatex])

  // Setup clipboard paste event listener
  useEffect(() => {
    const handlePaste = (e) => {
      if (mode === "image") {
        const items = e.clipboardData.items
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            const blob = items[i].getAsFile()
            const reader = new FileReader()
            reader.onload = (event) => {
              setSelectedImage(event.target.result)
            }
            reader.readAsDataURL(blob)
            break
          }
        }
      }
    }

    document.addEventListener("paste", handlePaste)
    return () => {
      document.removeEventListener("paste", handlePaste)
    }
  }, [mode])

  // Add this function to handle undo for the current page
  const handleUndo = () => {
    if (history[currentPage]?.length > 0) {
      // Get the previous state for the current page
      const previousState = history[currentPage][history[currentPage].length - 1]

      // Update annotations with the previous state
      setAnnotations((prev) => {
        const updatedAnnotations = { ...prev }
        updatedAnnotations[currentPage] = { ...previousState }
        return updatedAnnotations
      })

      // Remove the used state from history
      setHistory((prev) => {
        const updatedHistory = { ...prev }
        updatedHistory[currentPage] = updatedHistory[currentPage].slice(0, -1)
        return updatedHistory
      })
    }
  }

  // Add this function to save the current state to history before making changes
  const saveToHistory = () => {
    setHistory((prev) => {
      const updatedHistory = { ...prev }
      if (!updatedHistory[currentPage]) {
        updatedHistory[currentPage] = []
      }
      // Deep clone the current annotations for this page
      updatedHistory[currentPage] = [
        ...updatedHistory[currentPage],
        JSON.parse(JSON.stringify(annotations[currentPage] || { drawings: [], texts: [], images: [], deletions: [] })),
      ].slice(-10) // Keep only the last 10 states to limit memory usage
      return updatedHistory
    })
  }

  // Modify the handleKeyDown function to handle Ctrl+Z
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "z") {
      e.preventDefault()
      handleUndo()
      return
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      if (selectedAnnotation !== null && mode === "text") {
        e.preventDefault()

        // Save current state before deleting
        saveToHistory()

        setAnnotations((prev) => {
          const updatedAnnotations = { ...prev }
          if (updatedAnnotations[currentPage]?.texts) {
            updatedAnnotations[currentPage].texts = updatedAnnotations[currentPage].texts.filter(
              (_, index) => index !== selectedAnnotation,
            )
          }
          return updatedAnnotations
        })

        setSelectedAnnotation(null)
      } else if (selectedImageIndex !== null) {
        e.preventDefault()

        // Save current state before deleting
        saveToHistory()

        setAnnotations((prev) => {
          const updatedAnnotations = { ...prev }
          if (updatedAnnotations[currentPage]?.images) {
            updatedAnnotations[currentPage].images = updatedAnnotations[currentPage].images.filter(
              (_, index) => index !== selectedImageIndex,
            )
          }
          return updatedAnnotations
        })

        setSelectedImageIndex(null)
      }
    }
  }

  // Add this useEffect to handle keyboard events
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [selectedAnnotation, mode, currentPage, selectedImageIndex, history])

  // Canvas setup for drawing
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")

    // Clear canvas and redraw all annotations for current page
    const redrawCanvas = () => {
      if (!canvas || !ctx) return

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw all deletion rectangles first (so they appear under other annotations)
      if (annotations[currentPage]?.deletions) {
        annotations[currentPage].deletions.forEach((deletion) => {
          ctx.fillStyle = `rgba(255, 255, 255, ${deletion.opacity || 1})`
          // Apply scale to the deletion rectangle
          ctx.fillRect(deletion.x * scale, deletion.y * scale, deletion.width * scale, deletion.height * scale)
        })
      }

      // Draw all saved drawings
      if (annotations[currentPage]?.drawings) {
        annotations[currentPage].drawings.forEach((drawing) => {
          ctx.beginPath()
          ctx.strokeStyle = drawing.color
          ctx.lineWidth = drawing.size * scale // Scale the line width

          drawing.points.forEach((point, index) => {
            if (index === 0) {
              ctx.moveTo(point.x * scale, point.y * scale) // Scale the coordinates
            } else {
              ctx.lineTo(point.x * scale, point.y * scale) // Scale the coordinates
            }
          })

          ctx.stroke()
        })
      }

      // Draw all saved texts
      if (annotations[currentPage]?.texts) {
        annotations[currentPage].texts.forEach((text, index) => {
          // Scale the font size
          const scaledFontSize = (text.fontSize || 16) * scale

          if (text.isLatex) {
            // For LaTeX texts, we'll render a placeholder and handle actual rendering separately
            ctx.font = `${scaledFontSize}px ${text.fontFamily || "Arial"}`
            ctx.fillStyle = "#000000"
            ctx.fillText("", text.x * scale, text.y * scale) // Scale the coordinates
          } else {
            ctx.font = `${scaledFontSize}px ${text.fontFamily || "Arial"}`
            ctx.fillStyle = "#000000"

            // Highlight selected text
            if (selectedAnnotation === index && mode === "text") {
              const textWidth = ctx.measureText(text.content).width
              const textHeight = scaledFontSize

              // Draw highlight background
              ctx.fillStyle = "rgba(0, 123, 255, 0.2)"
              ctx.fillRect(text.x * scale - 2, text.y * scale - textHeight, textWidth + 4, textHeight + 4)

              // Draw text
              ctx.fillStyle = "#000000"
            }

            ctx.fillText(text.content, text.x * scale, text.y * scale) // Scale the coordinates
          }
        })
      }

      // Draw all saved images
      if (annotations[currentPage]?.images) {
        annotations[currentPage].images.forEach((img, index) => {
          if (img.element) {
            // Draw highlight for selected image
            if (selectedImageIndex === index) {
              ctx.strokeStyle = "rgba(0, 123, 255, 0.8)"
              ctx.lineWidth = 2

              if (img.shape === "circle") {
                const centerX = (img.x + img.width / 2) * scale
                const centerY = (img.y + img.height / 2) * scale
                const radius = (Math.min(img.width, img.height) / 2) * scale

                ctx.beginPath()
                ctx.arc(centerX, centerY, radius + 2, 0, Math.PI * 2)
                ctx.stroke()
              } else {
                // Rectangle or rounded rectangle
                ctx.strokeRect(img.x * scale - 2, img.y * scale - 2, img.width * scale + 4, img.height * scale + 4)
              }
            }

            // Save the current context state
            ctx.save()

            // Apply clipping path based on shape
            if (img.shape === "roundedRect") {
              const x = img.x * scale
              const y = img.y * scale
              const width = img.width * scale
              const height = img.height * scale
              const radius = Math.min(width, height) * 0.1 // 10% of the smaller dimension

              ctx.beginPath()
              ctx.moveTo(x + radius, y)
              ctx.lineTo(x + width - radius, y)
              ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
              ctx.lineTo(x + width, y + height - radius)
              ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
              ctx.lineTo(x + radius, y + height)
              ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
              ctx.lineTo(x, y + radius)
              ctx.quadraticCurveTo(x, y, x + radius, y)
              ctx.closePath()
              ctx.clip()
            } else if (img.shape === "circle") {
              const centerX = (img.x + img.width / 2) * scale
              const centerY = (img.y + img.height / 2) * scale
              const radius = (Math.min(img.width, img.height) / 2) * scale

              ctx.beginPath()
              ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
              ctx.clip()
            }

            // Scale the image dimensions and position
            ctx.drawImage(img.element, img.x * scale, img.y * scale, img.width * scale, img.height * scale)

            // Restore the context state
            ctx.restore()
          }
        })
      }

      // Draw selection shape if active
      if (isSelectingRectangle) {
        if (mode === "image") {
          ctx.strokeStyle = "#0000FF"
          ctx.lineWidth = 2
          ctx.setLineDash([5, 5])

          const width = rectangleEnd.x - rectangleStart.x
          const height = rectangleEnd.y - rectangleStart.y

          if (imageSelectionMode === "circle") {
            // Draw a circle using the rectangle as a bounding box
            const centerX = (rectangleStart.x + rectangleEnd.x) / 2
            const centerY = (rectangleStart.y + rectangleEnd.y) / 2
            const radius = Math.max(Math.abs(width), Math.abs(height)) / 2

            ctx.beginPath()
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
            ctx.stroke()
          } else if (imageSelectionMode === "roundedRect") {
            // Draw a rounded rectangle
            const x = Math.min(rectangleStart.x, rectangleEnd.x)
            const y = Math.min(rectangleStart.y, rectangleEnd.y)
            const w = Math.abs(width)
            const h = Math.abs(height)
            const radius = Math.min(w, h) * 0.1 // 10% of the smaller dimension

            ctx.beginPath()
            ctx.moveTo(x + radius, y)
            ctx.lineTo(x + w - radius, y)
            ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
            ctx.lineTo(x + w, y + h - radius)
            ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
            ctx.lineTo(x + radius, y + h)
            ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
            ctx.lineTo(x, y + radius)
            ctx.quadraticCurveTo(x, y, x + radius, y)
            ctx.closePath()
            ctx.stroke()
          } else {
            // Default rectangle
            const x = Math.min(rectangleStart.x, rectangleEnd.x)
            const y = Math.min(rectangleStart.y, rectangleEnd.y)
            const w = Math.abs(width)
            const h = Math.abs(height)

            ctx.strokeRect(x, y, w, h)
          }
        } else if (mode === "delete") {
          ctx.strokeStyle = "#FF0000"
          ctx.lineWidth = 2
          ctx.setLineDash([5, 5])

          const width = rectangleEnd.x - rectangleStart.x
          const height = rectangleEnd.y - rectangleStart.y
          const x = Math.min(rectangleStart.x, rectangleEnd.x)
          const y = Math.min(rectangleStart.y, rectangleEnd.y)
          const w = Math.abs(width)
          const h = Math.abs(height)

          ctx.strokeRect(x, y, w, h)

          // For delete mode, also show a semi-transparent white fill
          ctx.fillStyle = `rgba(255, 255, 255, 0.5)`
          ctx.fillRect(x, y, w, h)
        }

        ctx.setLineDash([])
      }
    }

    redrawCanvas()

    // Set canvas dimensions to match PDF page
    const updateCanvasSize = () => {
      if (containerRef.current && canvas) {
        const container = containerRef.current
        canvas.width = container.offsetWidth
        canvas.height = container.offsetHeight
        redrawCanvas()
      }
    }

    updateCanvasSize()
    window.addEventListener("resize", updateCanvasSize)

    return () => {
      window.removeEventListener("resize", updateCanvasSize)
    }
  }, [
    annotations,
    currentPage,
    isSelectingRectangle,
    rectangleStart,
    rectangleEnd,
    mode,
    selectedAnnotation,
    deleteOpacity,
    selectedImageIndex,
    scale, // Add scale as a dependency to redraw when it changes
  ])

  // Modify the startDrawing function to save state before drawing
  const startDrawing = (e) => {
    if (mode !== "draw") return

    // Save current state before drawing
    saveToHistory()

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX - rect.left) / scale // Store unscaled coordinates
    const y = (e.clientY - rect.top) / scale // Store unscaled coordinates

    setIsDrawing(true)

    // Start a new drawing
    const newDrawing = {
      color: drawColor,
      size: drawSize,
      points: [{ x, y }],
    }

    // Update annotations
    setAnnotations((prev) => {
      const updatedAnnotations = { ...prev }
      if (!updatedAnnotations[currentPage]) {
        updatedAnnotations[currentPage] = { drawings: [], texts: [], images: [], deletions: [] }
      }
      updatedAnnotations[currentPage].drawings = [...updatedAnnotations[currentPage].drawings, newDrawing]
      return updatedAnnotations
    })

    // Start drawing on canvas
    ctx.beginPath()
    ctx.strokeStyle = drawColor
    ctx.lineWidth = drawSize * scale // Apply scale to line width
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top) // Use actual screen coordinates for drawing
  }

  const draw = (e) => {
    if (!isDrawing || mode !== "draw") return

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX - rect.left) / scale // Store unscaled coordinates
    const y = (e.clientY - rect.top) / scale // Store unscaled coordinates

    // Update the last drawing with new point
    setAnnotations((prev) => {
      const updatedAnnotations = { ...prev }
      const drawings = updatedAnnotations[currentPage].drawings
      const lastDrawing = drawings[drawings.length - 1]
      lastDrawing.points.push({ x, y })
      return updatedAnnotations
    })

    // Draw on canvas
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top) // Use actual screen coordinates for drawing
    ctx.stroke()
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  // Modify the handleMouseDown function to save state before rectangle selection
  const handleMouseDown = (e) => {
    if (mode === "draw") {
      startDrawing(e)
    } else if (mode === "image" && selectedImage && imageSelectionMode === "rectangle") {
      // Save current state before adding image
      saveToHistory()

      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      setRectangleStart({ x, y })
      setRectangleEnd({ x, y })
      setIsSelectingRectangle(true)
    } else if (mode === "delete") {
      // Save current state before deleting
      saveToHistory()

      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      setRectangleStart({ x, y })
      setRectangleEnd({ x, y })
      setIsSelectingRectangle(true)
    }
  }

  // Handle mouse move for different modes
  const handleMouseMove = (e) => {
    if (mode === "draw") {
      draw(e)
    } else if (isSelectingRectangle && (mode === "image" || mode === "delete")) {
      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      setRectangleEnd({ x, y })

      // Draw the selection shape differently based on imageSelectionMode
      if (isSelectingRectangle && mode === "image") {
        const canvas = canvasRef.current
        const ctx = canvas.getContext("2d")

        // Clear the canvas and redraw everything
        // This will be handled by the useEffect that depends on rectangleStart and rectangleEnd
      }
    }
  }

  // Handle mouse up for different modes
  const handleMouseUp = (e) => {
    if (mode === "draw") {
      stopDrawing()
    } else if (isSelectingRectangle && mode === "image" && selectedImage) {
      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      setRectangleEnd({ x, y })
      setIsSelectingRectangle(false)

      // Create image with the selected dimensions
      const img = new window.Image()
      img.crossOrigin = "anonymous"
      img.src = selectedImage

      img.onload = () => {
        // Calculate dimensions from the rectangle
        let width = Math.abs(rectangleEnd.x - rectangleStart.x) / scale // Store unscaled width
        let height = Math.abs(rectangleEnd.y - rectangleStart.y) / scale // Store unscaled height

        // Use the top-left corner of the rectangle
        let x = Math.min(rectangleStart.x, rectangleEnd.x) / scale // Store unscaled x
        let y = Math.min(rectangleStart.y, rectangleEnd.y) / scale // Store unscaled y

        // For circle, use the center and radius
        if (imageSelectionMode === "circle") {
          // Calculate center and radius
          const centerX = (rectangleStart.x + rectangleEnd.x) / 2 / scale
          const centerY = (rectangleStart.y + rectangleEnd.y) / 2 / scale
          const maxDimension =
            Math.max(Math.abs(rectangleEnd.x - rectangleStart.x), Math.abs(rectangleEnd.y - rectangleStart.y)) / scale
          const radius = maxDimension / 2

          // Adjust x, y, width, height to make a perfect circle
          x = centerX - radius
          y = centerY - radius
          width = radius * 2
          height = radius * 2
        }

        setAnnotations((prev) => {
          const updatedAnnotations = { ...prev }
          if (!updatedAnnotations[currentPage]) {
            updatedAnnotations[currentPage] = { drawings: [], texts: [], images: [], deletions: [] }
          }
          updatedAnnotations[currentPage].images.push({
            element: img,
            x,
            y,
            width: width || 200 / scale, // Default if width is 0, store unscaled
            height: height || ((200 / img.width) * img.height) / scale, // Default if height is 0, store unscaled
            shape: imageSelectionMode, // Store the shape type
          })
          return updatedAnnotations
        })

        // Reset selected image
        setSelectedImage(null)
      }
    } else if (isSelectingRectangle && mode === "delete") {
      setIsSelectingRectangle(false)

      // Calculate dimensions from the rectangle
      const width = Math.abs(rectangleEnd.x - rectangleStart.x) / scale // Store unscaled width
      const height = Math.abs(rectangleEnd.y - rectangleStart.y) / scale // Store unscaled height

      // Use the top-left corner of the rectangle
      const x = Math.min(rectangleStart.x, rectangleEnd.x) / scale // Store unscaled x
      const y = Math.min(rectangleStart.y, rectangleEnd.y) / scale // Store unscaled y

      // Add a white rectangle to "delete" content
      setAnnotations((prev) => {
        const updatedAnnotations = { ...prev }
        if (!updatedAnnotations[currentPage]) {
          updatedAnnotations[currentPage] = { drawings: [], texts: [], images: [], deletions: [] }
        }
        updatedAnnotations[currentPage].deletions.push({
          x,
          y,
          width,
          height,
          opacity: deleteOpacity,
        })
        return updatedAnnotations
      })
    }
  }

  // Modify the handleCanvasClick function to handle text selection
  const handleCanvasClick = (e) => {
    if (mode === "text") {
      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const unscaledX = x / scale // Store unscaled x
      const unscaledY = y / scale // Store unscaled y

      // Check if we clicked on an existing text annotation
      if (annotations[currentPage]?.texts) {
        const clickedTextIndex = annotations[currentPage].texts.findIndex((text) => {
          // Create a temporary canvas context to measure text width
          const tempCanvas = document.createElement("canvas")
          const tempCtx = tempCanvas.getContext("2d")
          tempCtx.font = `${(text.fontSize || 16) * scale}px ${text.fontFamily || "Arial"}`
          const textWidth = tempCtx.measureText(text.content).width
          const textHeight = (text.fontSize || 16) * scale

          // Check if click is within text bounds (with some padding)
          return (
            x >= text.x * scale - 5 &&
            x <= text.x * scale + textWidth + 5 &&
            y >= text.y * scale - textHeight &&
            y <= text.y * scale + 5
          )
        })

        if (clickedTextIndex !== -1) {
          setSelectedAnnotation(clickedTextIndex)
          setSelectedImageIndex(null)
          return
        }
      }

      // If we didn't click on existing text, set position for new text
      setTextPosition({
        x: unscaledX,
        y: unscaledY,
      })
      setShowTextPosition(true)
      setSelectedAnnotation(null)
      setSelectedImageIndex(null)

      // Auto-focus the text input
      const textInputElement = document.querySelector('input[type="text"][placeholder="Enter text or LaTeX..."]')
      if (textInputElement) {
        textInputElement.focus()
      }
    } else if (mode === "image" && selectedImage && imageSelectionMode === "click") {
      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const unscaledX = (e.clientX - rect.left) / scale // Store unscaled x
      const unscaledY = (e.clientY - rect.top) / scale // Store unscaled y

      setImagePosition({
        x: unscaledX,
        y: unscaledY,
      })

      // Create image element and add to annotations
      const img = new window.Image()
      img.crossOrigin = "anonymous"
      img.src = selectedImage

      img.onload = () => {
        // Calculate proportional dimensions
        const maxWidth = 200 / scale // Unscaled max width
        const ratio = img.width / img.height
        const width = Math.min(maxWidth, img.width / scale) // Unscaled width
        const height = width / ratio // Unscaled height

        setAnnotations((prev) => {
          const updatedAnnotations = { ...prev }
          if (!updatedAnnotations[currentPage]) {
            updatedAnnotations[currentPage] = { drawings: [], texts: [], images: [], deletions: [] }
          }
          updatedAnnotations[currentPage].images.push({
            element: img,
            x: imagePosition.x,
            y: imagePosition.y,
            width,
            height,
          })
          return updatedAnnotations
        })

        // Reset selected image
        setSelectedImage(null)
      }
    } else if (mode === "view") {
      // Check if we clicked on an existing image
      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      if (annotations[currentPage]?.images) {
        const clickedImageIndex = annotations[currentPage].images.findIndex((img) => {
          return (
            x >= img.x * scale &&
            x <= img.x * scale + img.width * scale &&
            y >= img.y * scale &&
            y <= img.y * scale + img.height * scale
          )
        })

        if (clickedImageIndex !== -1) {
          setSelectedImageIndex(clickedImageIndex)
          setSelectedAnnotation(null)
          return
        }
      }

      // If we didn't click on an image, clear selection
      setSelectedImageIndex(null)
      setSelectedAnnotation(null)
    }
  }

  // Modify the addTextAnnotation function to save state before adding text
  const addTextAnnotation = () => {
    if (!textInput.trim()) return

    // Save current state before adding text
    saveToHistory()

    setAnnotations((prev) => {
      const updatedAnnotations = { ...prev }
      if (!updatedAnnotations[currentPage]) {
        updatedAnnotations[currentPage] = { drawings: [], texts: [], images: [], deletions: [] }
      }
      updatedAnnotations[currentPage].texts.push({
        content: textInput,
        x: textPosition.x,
        y: textPosition.y,
        isLatex: isLatex,
        fontFamily: selectedFont,
        fontSize: fontSize,
      })
      return updatedAnnotations
    })

    // Reset text input and hide the indicator
    setTextInput("")
    setShowTextPosition(false)
  }

  // Add a function to undo the last added image
  const undoLastImage = () => {
    if (annotations[currentPage]?.images?.length > 0) {
      // Save current state before undoing
      saveToHistory()

      setAnnotations((prev) => {
        const updatedAnnotations = { ...prev }
        updatedAnnotations[currentPage].images.pop()
        return updatedAnnotations
      })
    }
  }

  // Image handling
  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setSelectedImage(event.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  // Handle paste from clipboard
  const handlePasteButtonClick = () => {
    // Show a message to the user about using Ctrl+V
    alert(
      "Please use Ctrl+V (or Cmd+V on Mac) to paste an image directly onto the PDF viewer. Browser security restrictions prevent automatic clipboard access.",
    )

    // Focus the canvas to make it easier to paste
    if (canvasRef.current) {
      canvasRef.current.focus()
    }
  }

  // Navigation
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const goToNextPage = () => {
    if (currentPage < numPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handleSliderChange = (e) => {
    const newPage = Number.parseInt(e.target.value)
    setCurrentPage(newPage)
  }

  // Zoom controls
  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 3))
  }

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5))
  }

  // Add useEffect to hide the text position indicator when changing modes
  useEffect(() => {
    if (mode !== "text") {
      setShowTextPosition(false)
    }
  }, [mode])

  // Function to handle mode change
  const handleModeChange = (newMode) => {
    setMode(newMode)
    setIsSelectingRectangle(false)
    setSelectedAnnotation(null)
  }

  // Function to undo the last deletion
  const undoLastDeletion = () => {
    setAnnotations((prev) => {
      const updatedAnnotations = { ...prev }
      if (updatedAnnotations[currentPage]?.deletions?.length > 0) {
        updatedAnnotations[currentPage].deletions.pop()
      }
      return updatedAnnotations
    })
  }

  // Function to clear all deletions
  const clearAllDeletions = () => {
    setAnnotations((prev) => {
      const updatedAnnotations = { ...prev }
      if (updatedAnnotations[currentPage]) {
        updatedAnnotations[currentPage].deletions = []
      }
      return updatedAnnotations
    })
  }

  // Add a function to save the PDF with annotations
  const savePDFWithChanges = async () => {
    if (!file) return

    try {
      // Show loading indicator
      alert("Generating PDF... This may take a moment.")

      // Get the jsPDF library
      const jsPDF = (await import("jspdf")).default

      // Store the current page and scale to restore later
      const currentPageBackup = currentPage
      const currentScaleBackup = scale

      // First, determine the dimensions of the original PDF
      const pdfDimensions = []

      // Process each page to get dimensions
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        // Set the current page to render it
        setCurrentPage(pageNum)

        // Wait for the page to render
        await new Promise((resolve) => setTimeout(resolve, 300))

        // Get the PDF page element
        const pdfElement = containerRef.current.querySelector(".react-pdf__Page")
        if (pdfElement) {
          const pdfCanvas = pdfElement.querySelector("canvas")
          if (pdfCanvas) {
            pdfDimensions.push({
              width: pdfCanvas.width,
              height: pdfCanvas.height,
              isLandscape: pdfCanvas.width > pdfCanvas.height,
            })
          }
        }
      }

      // Create a new PDF document with the orientation of the first page
      const firstPage = pdfDimensions[0] || { width: 595, height: 842, isLandscape: false }
      const pdfOutput = new jsPDF({
        orientation: firstPage.isLandscape ? "landscape" : "portrait",
        unit: "pt",
        format: [firstPage.width, firstPage.height],
      })

      // Process each page again to capture and add to PDF
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        // Set the current page to render it
        setCurrentPage(pageNum)

        // Wait for the page to render
        await new Promise((resolve) => setTimeout(resolve, 300))

        const pageDimensions = pdfDimensions[pageNum - 1]
        if (!pageDimensions) continue

        // Get the page elements
        const container = containerRef.current
        const canvas = canvasRef.current
        const pdfElement = container.querySelector(".react-pdf__Page")

        if (container && canvas && pdfElement) {
          // Create a new canvas to capture the entire view at the original PDF dimensions
          const captureCanvas = document.createElement("canvas")
          captureCanvas.width = pageDimensions.width
          captureCanvas.height = pageDimensions.height

          const captureCtx = captureCanvas.getContext("2d")

          // Draw the background (white)
          captureCtx.fillStyle = "#ffffff"
          captureCtx.fillRect(0, 0, pageDimensions.width, pageDimensions.height)

          // Get the PDF page canvas
          const pdfCanvas = pdfElement.querySelector("canvas")
          if (pdfCanvas) {
            // Calculate the scale factor to fit the PDF page into our capture canvas
            const scaleX = pageDimensions.width / pdfCanvas.width
            const scaleY = pageDimensions.height / pdfCanvas.height

            // Draw the PDF page canvas to our capture canvas
            captureCtx.drawImage(
              pdfCanvas,
              0,
              0,
              pdfCanvas.width,
              pdfCanvas.height,
              0,
              0,
              pageDimensions.width,
              pageDimensions.height,
            )

            // Scale and draw the annotations canvas on top
            captureCtx.save()
            captureCtx.scale(scaleX, scaleY)

            // We need to redraw the annotations at the correct scale
            // First, save the current scale
            const originalScale = scale

            // Set scale to match the PDF page
            const tempScale = scale * (pageDimensions.width / pdfCanvas.width)

            // Redraw annotations at the correct scale
            const ctx = canvas.getContext("2d")
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            // Draw all deletion rectangles
            if (annotations[pageNum]?.deletions) {
              annotations[pageNum].deletions.forEach((deletion) => {
                captureCtx.fillStyle = `rgba(255, 255, 255, ${deletion.opacity || 1})`
                captureCtx.fillRect(
                  deletion.x * tempScale,
                  deletion.y * tempScale,
                  deletion.width * tempScale,
                  deletion.height * tempScale,
                )
              })
            }

            // Draw all saved drawings
            if (annotations[pageNum]?.drawings) {
              annotations[pageNum].drawings.forEach((drawing) => {
                captureCtx.beginPath()
                captureCtx.strokeStyle = drawing.color
                captureCtx.lineWidth = drawing.size * tempScale

                drawing.points.forEach((point, index) => {
                  if (index === 0) {
                    captureCtx.moveTo(point.x * tempScale, point.y * tempScale)
                  } else {
                    captureCtx.lineTo(point.x * tempScale, point.y * tempScale)
                  }
                })

                captureCtx.stroke()
              })
            }

            // Draw all saved texts
            if (annotations[pageNum]?.texts) {
              for (const text of annotations[pageNum].texts) {
                const scaledFontSize = (text.fontSize || 16) * tempScale

                if (text.isLatex) {
                  // For LaTeX, render it directly to the capture canvas
                  captureCtx.font = `${scaledFontSize}px ${text.fontFamily || "Arial"}`
                  captureCtx.fillStyle = "#000000"

                  // Use KaTeX to render the LaTeX directly to the canvas
                  const latexHtml = katex.renderToString(text.content, {
                    throwOnError: false,
                    fontSize: scaledFontSize,
                  })

                  // Create a temporary div to measure the LaTeX
                  const tempDiv = document.createElement("div")
                  tempDiv.innerHTML = latexHtml
                  tempDiv.style.position = "absolute"
                  tempDiv.style.left = "-9999px"
                  tempDiv.style.top = "-9999px"
                  document.body.appendChild(tempDiv)

                  // Draw a placeholder for LaTeX (since we can't easily render it to canvas)
                  captureCtx.fillText(`${text.content}`, text.x * tempScale, text.y * tempScale)

                  // Clean up
                  document.body.removeChild(tempDiv)
                } else {
                  captureCtx.font = `${scaledFontSize}px ${text.fontFamily || "Arial"}`
                  captureCtx.fillStyle = "#000000"
                  captureCtx.fillText(text.content, text.x * tempScale, text.y * tempScale)
                }
              }
            }

            // Draw all saved images
            if (annotations[pageNum]?.images) {
              annotations[pageNum].images.forEach((img) => {
                if (img.element) {
                  captureCtx.drawImage(
                    img.element,
                    img.x * tempScale,
                    img.y * tempScale,
                    img.width * tempScale,
                    img.height * tempScale,
                  )
                }
              })
            }

            captureCtx.restore()

            // Get the image data
            const imgData = captureCanvas.toDataURL("image/jpeg", 1.0)

            // Add a new page for all pages after the first
            if (pageNum > 1) {
              // Add a page with the same dimensions as the current page
              pdfOutput.addPage([pageDimensions.width, pageDimensions.height])
            }

            // Add the image to the PDF
            pdfOutput.addImage(imgData, "JPEG", 0, 0, pageDimensions.width, pageDimensions.height)
          }
        }
      }

      // Restore the original page and scale
      setCurrentPage(currentPageBackup)
      setScale(currentScaleBackup)

      // Save the PDF
      pdfOutput.save("annotated-document.pdf")
    } catch (error) {
      console.error("Error saving PDF:", error)
      alert("There was an error saving the PDF. Please try again.")
    }
  }

  return (
    <div className="flex flex-col w-full h-full">
      <style>{rangeStyles}</style>
      <div className="flex justify-between items-center mb-4 p-3 bg-gray-50 rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <button
            className="p-2 border border-gray-300 rounded-md text-sm flex items-center gap-1 hover:bg-gray-100 transition-colors"
            onClick={zoomOut}
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium bg-white px-3 py-1 rounded-md border">{Math.round(scale * 100)}%</span>
          <button
            className="p-2 border border-gray-300 rounded-md text-sm flex items-center gap-1 hover:bg-gray-100 transition-colors"
            onClick={zoomIn}
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button
            className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            onClick={goToPreviousPage}
            disabled={currentPage <= 1}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            Previous
          </button>
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <span className="text-sm font-medium whitespace-nowrap">Page 1</span>
            <div className="relative flex-1 group">
              <input
                type="range"
                min={1}
                max={numPages || 1}
                value={currentPage}
                onChange={handleSliderChange}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="absolute -top-6 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity text-center">
                <span className="text-xs bg-gray-800 text-white px-2 py-1 rounded">Page {currentPage}</span>
              </div>
            </div>
            <span className="text-sm font-medium whitespace-nowrap">Page {numPages || 1}</span>
          </div>
          <button
            className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            onClick={goToNextPage}
            disabled={currentPage >= numPages}
          >
            Next
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Improved tabs implementation */}
      <div className="mb-4">
        <div className="flex border-b bg-gray-50 rounded-t-lg overflow-hidden">
          <button
            className={`px-4 py-3 flex items-center gap-2 transition-colors ${
              mode === "view" ? "bg-white border-b-2 border-blue-500 font-medium" : "hover:bg-gray-100"
            }`}
            onClick={() => handleModeChange("view")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View
          </button>

          {/* Tool buttons */}
          <div className="flex ml-2 items-center">
            <button
              className={`p-2 m-1 rounded-md flex flex-col items-center justify-center transition-all ${
                mode === "draw" ? "bg-blue-100 border border-blue-300 shadow-sm" : "hover:bg-gray-100"
              }`}
              onClick={() => handleModeChange("draw")}
              title="Draw"
            >
              <Eraser className={`h-5 w-5 ${mode === "draw" ? "text-blue-600" : "text-gray-700"}`} />
              <span className="text-xs mt-1">Draw</span>
            </button>

            <button
              className={`p-2 m-1 rounded-md flex flex-col items-center justify-center transition-all ${
                mode === "text" ? "bg-blue-100 border border-blue-300 shadow-sm" : "hover:bg-gray-100"
              }`}
              onClick={() => handleModeChange("text")}
              title="Add Text"
            >
              <Type className={`h-5 w-5 ${mode === "text" ? "text-blue-600" : "text-gray-700"}`} />
              <span className="text-xs mt-1">Text</span>
            </button>

            <button
              className={`p-2 m-1 rounded-md flex flex-col items-center justify-center transition-all ${
                mode === "image" ? "bg-blue-100 border border-blue-300 shadow-sm" : "hover:bg-gray-100"
              }`}
              onClick={() => handleModeChange("image")}
              title="Add Image"
            >
              <Image className={`h-5 w-5 ${mode === "image" ? "text-blue-600" : "text-gray-700"}`} />
              <span className="text-xs mt-1">Image</span>
            </button>

            <button
              className={`p-2 m-1 rounded-md flex flex-col items-center justify-center transition-all ${
                mode === "delete" ? "bg-blue-100 border border-blue-300 shadow-sm" : "hover:bg-gray-100"
              }`}
              onClick={() => handleModeChange("delete")}
              title="Delete Content"
            >
              <Trash2 className={`h-5 w-5 ${mode === "delete" ? "text-blue-600" : "text-gray-700"}`} />
              <span className="text-xs mt-1">Delete</span>
            </button>
          </div>
        </div>

        {/* Tab content with improved styling */}
        <div className="p-3 border border-t-0 rounded-b-lg bg-white shadow-sm">
          {mode === "draw" && (
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={drawColor}
                onChange={(e) => setDrawColor(e.target.value)}
                className="w-10 h-10 p-1 border rounded-md cursor-pointer"
              />
              <select
                value={drawSize}
                onChange={(e) => setDrawSize(Number(e.target.value))}
                className="p-2 pr-8 border rounded-md bg-white"
              >
                <option value="1">Thin   </option>
                <option value="2">Medium   </option>
                <option value="4">Thick   </option>
                <option value="6">Thicker   </option>
                <option value="8">TTThicker   </option>
              </select>
              <button
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm flex items-center gap-1 transition-colors"
                onClick={() => {
                  setAnnotations((prev) => {
                    const updatedAnnotations = { ...prev }
                    if (updatedAnnotations[currentPage]) {
                      updatedAnnotations[currentPage].drawings = []
                    }
                    return updatedAnnotations
                  })
                }}
              >
                <Eraser className="h-4 w-4 mr-1" /> Clear Drawings
              </button>
            </div>
          )}
          {mode === "draw" && (
            <div className="text-xs text-gray-500 italic mt-3">
              Tip: Press Ctrl+Z (or Cmd+Z on Mac) to undo any action.
            </div>
          )}

          {mode === "text" && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Enter text or LaTeX..."
                  className="p-2 border rounded-md flex-1"
                />
                <div className="flex items-center bg-gray-50 p-2 rounded-md">
                  <input
                    type="checkbox"
                    id="latex-toggle"
                    checked={isLatex}
                    onChange={() => setIsLatex(!isLatex)}
                    className="mr-2"
                  />
                  <label htmlFor="latex-toggle" className="text-sm font-medium">
                    LaTeX
                  </label>
                </div>
                <button
                  className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-sm flex items-center gap-1 transition-colors"
                  onClick={addTextAnnotation}
                >
                  <Type className="h-4 w-4 mr-1" /> Add Text
                </button>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={selectedFont}
                  onChange={(e) => setSelectedFont(e.target.value)}
                  className="p-2 pr-8 border rounded-md bg-white"
                >
                  <option value="Arial">Arial</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Tahoma">Tahoma</option>
                </select>

                <select
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                  className="p-2 pr-8 border rounded-md bg-white"
                >
                  <option value="10">10px</option>
                  <option value="12">12px</option>
                  <option value="14">14px</option>
                  <option value="16">16px</option>
                  <option value="18">18px</option>
                  <option value="20">20px</option>
                  <option value="24">24px</option>
                  <option value="28">28px</option>
                  <option value="32">32px</option>
                  <option value="36">36px</option>
                  <option value="48">48px</option>
                </select>

                {selectedAnnotation !== null && (
                  <div className="ml-auto flex items-center bg-blue-50 p-2 rounded-md">
                    <span className="text-sm text-blue-600 mr-2">Text selected. Press Backspace to delete.</span>
                    <button
                      className="px-3 py-1 bg-red-50 border border-red-300 text-red-600 hover:bg-red-100 rounded-md text-sm transition-colors"
                      onClick={() => {
                        setAnnotations((prev) => {
                          const updatedAnnotations = { ...prev }
                          if (updatedAnnotations[currentPage]?.texts) {
                            updatedAnnotations[currentPage].texts = updatedAnnotations[currentPage].texts.filter(
                              (_, index) => index !== selectedAnnotation,
                            )
                          }
                          return updatedAnnotations
                        })
                        setSelectedAnnotation(null)
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>

              {isLatex && textInput && (
                <div className="border p-3 rounded-md bg-white shadow-sm">
                  <div className="text-sm font-medium mb-1 text-gray-700">LaTeX Preview:</div>
                  <div
                    ref={latexPreviewRef}
                    className="overflow-auto max-h-20 p-2 bg-gray-50 rounded-md"
                    style={{ fontSize: `${fontSize}px`, fontFamily: selectedFont }}
                  ></div>
                </div>
              )}

              {!isLatex && textInput && (
                <div className="border p-3 rounded-md bg-white shadow-sm">
                  <div className="text-sm font-medium mb-1 text-gray-700">Text Preview:</div>
                  <div
                    className="overflow-auto max-h-20 p-2 bg-gray-50 rounded-md"
                    style={{ fontSize: `${fontSize}px`, fontFamily: selectedFont }}
                  >
                    {textInput}
                  </div>
                </div>
              )}
            </div>
          )}
          {mode === "text" && !isLatex && !textInput && (
            <div className="text-xs text-gray-500 italic mt-1">
              Tip: Press Ctrl+Z (or Cmd+Z on Mac) to undo any action.
            </div>
          )}

          {mode === "image" && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-sm flex items-center gap-1 transition-colors"
                  onClick={() => fileInputRef.current.click()}
                >
                  <Image className="h-4 w-4 mr-1" /> Select Image
                </button>
                <button
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm flex items-center gap-1 transition-colors"
                  onClick={handlePasteButtonClick}
                >
                  <Clipboard className="h-4 w-4 mr-1" /> Paste Image
                </button>
                <button
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={undoLastImage}
                  disabled={!annotations[currentPage]?.images?.length}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 7v6h6" />
                    <path d="M3 13c0-4.4 3.6-8 8-8 3.7 0 6.8 2.5 7.8 6" />
                  </svg>
                  Undo Last Image
                </button>
                <div className="ml-4 flex items-center gap-2 bg-gray-50 p-2 rounded-md">
                  <div className="text-sm font-medium">Shape:</div>
                  <button
                    className={`px-3 py-1 border rounded-md text-sm flex items-center gap-1 transition-colors ${imageSelectionMode === "rectangle" ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"}`}
                    onClick={() => setImageSelectionMode("rectangle")}
                  >
                    <Square className="h-4 w-4 mr-1" /> Rectangle
                  </button>
                  <button
                    className={`px-3 py-1 border rounded-md text-sm flex items-center gap-1 transition-colors ${imageSelectionMode === "roundedRect" ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"}`}
                    onClick={() => setImageSelectionMode("roundedRect")}
                  >
                    <Square className="h-4 w-4 mr-1" /> Rounded
                  </button>
                  <button
                    className={`px-3 py-1 border rounded-md text-sm flex items-center gap-1 transition-colors ${imageSelectionMode === "circle" ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"}`}
                    onClick={() => setImageSelectionMode("circle")}
                  >
                    <Circle className="h-4 w-4 mr-1" /> Circle
                  </button>
                </div>
              </div>
              {selectedImage && (
                <div className="flex items-center gap-3 bg-green-50 p-3 rounded-md border border-green-100">
                  <div className="w-12 h-12 border rounded-md overflow-hidden shadow-sm">
                    <img
                      src={selectedImage || "/placeholder.svg"}
                      alt="Selected"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-sm text-green-700 font-medium">
                    Image selected. Draw a{" "}
                    {imageSelectionMode === "circle"
                      ? "circle"
                      : imageSelectionMode === "roundedRect"
                        ? "rounded rectangle"
                        : "rectangle"}{" "}
                    on the PDF to place it.
                  </span>
                </div>
              )}
              <div className="text-xs text-gray-500 italic mt-1">
                Tip: Press Ctrl+Z (or Cmd+Z on Mac) to undo any action.
              </div>
            </div>
          )}

          {mode === "delete" && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="text-sm font-medium">Draw rectangles to delete content from the PDF:</div>
                <div className="flex items-center ml-4 bg-gray-50 p-2 rounded-md">
                  <span className="text-sm font-medium mr-2">Opacity:</span>
                  <input
                    type="range"
                    min="0.5"
                    max="1"
                    step="0.1"
                    value={deleteOpacity}
                    onChange={(e) => setDeleteOpacity(Number.parseFloat(e.target.value))}
                    className="w-24"
                  />
                  <span className="text-sm ml-1">{Math.round(deleteOpacity * 100)}%</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={undoLastDeletion}
                  disabled={!annotations[currentPage]?.deletions?.length}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 7v6h6" />
                    <path d="M3 13c0-4.4 3.6-8 8-8 3.7 0 6.8 2.5 7.8 6" />
                  </svg>
                  Undo Last
                </button>
                <button
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={clearAllDeletions}
                  disabled={!annotations[currentPage]?.deletions?.length}
                >
                  Clear All Deletions
                </button>
                <div className="ml-auto text-sm text-gray-600 bg-gray-50 p-2 rounded-md">
                  <Trash2 className="h-4 w-4 inline mr-1" />
                  Draw a rectangle over content to delete it
                </div>
              </div>
            </div>
          )}
          {mode === "delete" && (
            <div className="text-xs text-gray-500 italic mt-3">
              Tip: Press Ctrl+Z (or Cmd+Z on Mac) to undo any action.
            </div>
          )}
        </div>
      </div>

      <div
        ref={containerRef}
        className="relative flex-1 overflow-auto border border-gray-300 bg-gray-100"
        style={{ minHeight: "500px", paddingBottom: "100px" }}
      >
        {file ? (
          <>
            <Document file={file} onLoadSuccess={onDocumentLoadSuccess}>
              <Page
                pageNumber={currentPage}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                scale={scale}
                width={containerRef.current?.offsetWidth}
              />
            </Document>

            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 w-full h-full pointer-events-auto"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onClick={handleCanvasClick}
              tabIndex={0}
              style={{
                cursor:
                  mode === "draw"
                    ? "crosshair"
                    : mode === "text"
                      ? "text"
                      : mode === "image" && imageSelectionMode === "rectangle"
                        ? "crosshair"
                        : mode === "image"
                          ? "copy"
                          : mode === "delete"
                            ? "crosshair"
                            : "default",
              }}
            />

            {/* Text position indicator */}
            {showTextPosition && mode === "text" && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: `${textPosition.x * scale}px`,
                  top: `${textPosition.y * scale - 16}px`,
                }}
              >
                <div className="bg-black/10 px-2 py-1 rounded text-xs whitespace-nowrap">
                  {isLatex ? "LaTeX" : "Text"} will appear here
                </div>
                {textInput && (
                  <div
                    className="mt-1 px-2 py-1 bg-white/90 border border-gray-200 rounded shadow-sm"
                    style={{
                      fontSize: `${fontSize * scale}px`,
                      fontFamily: selectedFont,
                    }}
                  >
                    {isLatex ? <div ref={latexPreviewRef}></div> : textInput}
                  </div>
                )}
              </div>
            )}

            {/* Render LaTeX annotations */}
            {annotations[currentPage]?.texts
              ?.filter((text) => text.isLatex)
              .map((text, index) => {
                const actualIndex = annotations[currentPage].texts.findIndex((t) => t === text)
                const isSelected = selectedAnnotation === actualIndex && mode === "text"

                return (
                  <div
                    key={`latex-${index}`}
                    className={`absolute pointer-events-none ${isSelected ? "bg-blue-100 p-1" : ""}`}
                    style={{
                      left: `${text.x * scale}px`,
                      top: `${text.y * scale - (text.fontSize || 16) * scale}px`,
                      fontSize: `${(text.fontSize || 16) * scale}px`,
                      fontFamily: text.fontFamily || "Arial",
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: katex.renderToString(text.content, {
                          throwOnError: false,
                          fontSize: (text.fontSize || 16) * scale,
                        }),
                      }}
                    />
                  </div>
                )
              })}
            {selectedImageIndex !== null && (
              <div className="absolute top-2 left-2 bg-white/80 p-2 rounded shadow z-10">
                <p className="text-sm text-blue-600">Image selected. Press Delete or Backspace to remove.</p>
                <button
                  className="mt-1 px-2 py-1 border border-red-500 text-red-500 rounded text-sm"
                  onClick={() => {
                    setAnnotations((prev) => {
                      const updatedAnnotations = { ...prev }
                      if (updatedAnnotations[currentPage]?.images) {
                        updatedAnnotations[currentPage].images = updatedAnnotations[currentPage].images.filter(
                          (_, index) => index !== selectedImageIndex,
                        )
                      }
                      return updatedAnnotations
                    })
                    setSelectedImageIndex(null)
                  }}
                >
                  Delete Image
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p>No PDF to display</p>
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md flex items-center gap-2 transition-colors shadow-sm"
          onClick={savePDFWithChanges}
        >
          <Save className="h-5 w-5" /> Save PDF with Changes
        </button>
      </div>
    </div>
  )
}

export default PdfViewer


